import React, { Component } from 'react';
import { withStyles  } from '@mui/styles';
import PropTypes from 'prop-types';
import { isEmpty, startCase, lowerCase } from 'lodash';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from '@mui/material/Typography';
import { Settings, ExpandMore } from '@mui/icons-material';
import Chip from "@mui/material/Chip";
import { amber, blue, green, red } from '@mui/material/colors';
import Button from "@mui/material/Button";
import { GAM_LINE_PRIMARY } from "../../utils/link";
import Tooltip from "@mui/material/Tooltip";


const styles = theme => ({
  root: {
    flexGrow: 1
  },
  head: {
    display: 'flex',
    flexFlow: 'wrap'
  },
  label: {
    marginTop: 10,
    padding: 10,
    background: theme.palette.primary.light,
    maxWidth: 800
  },
  value: {
    marginTop: 10,
    padding: 10,
    minHeight: 44,
    maxWidth: 800
  },
  labelFont: {
    color: theme.palette.primary.dark,
    fontWeight: 700
  },
  valueFont: {
    fontWeight: 400
  },
  top: {
    marginTop: 20
  },
  leftIcon: {
    marginRight: theme.spacing(2)
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 700
  },
  success: {
    background: green[600],
    color: theme.palette.common.white
  },
  info: {
    background: blue.A200,
    color: theme.palette.common.white
  },
  warn: {
    background: amber.A400,
    color: theme.palette.common.white
  },
  error: {
    background: red.A200,
    color: theme.palette.common.white
  },
});

class AdManagerView extends Component {

  renderLabel = key => {
    const { classes } = this.props;
    return (
      <Card square key={key} className={classes.label}>
        <Typography variant="subtitle1" className={classes.labelFont}>
          {startCase(lowerCase(key))}
        </Typography>
      </Card>
    )
  };

  renderValue = (key, settings) => {
    const { classes } = this.props;
    if (key.slice(0, 2) === '__') {
      return null;
    }
    if (key === 'setCustomTargeting') {
      settings[key] = settings[key].replaceAll('||', 'OR').replaceAll('|', 'AND');
    }
    return (
      <Card square key={key} className={classes.value}>
        <Typography variant="subtitle1" className={classes.valueFont}>
          {Array.isArray(settings[key]) ? JSON.stringify(settings[key]): settings[key]}
        </Typography>
      </Card>
    )
  };

  /**
   * Render the Ad Manager payload(s) for this placement.
   * Could be 0 to 3 depending on the variant.
   * Cannot render if there are no line items.
   * @returns {React.JSX.Element|null}
   */
  render() {
    const { classes, variant, placement } = this.props;
    if (!placement || !placement.LineItems || placement.LineItems.length === 0) {
      return null;
    }
    const li = placement.LineItems[variant - 1];
    let payload;
    if (typeof li.adManagerPayload === 'string') {
      payload = JSON.parse(li.adManagerPayload);
    } else {
      payload = Object.assign({}, li.adManagerPayload);
    }
    // Clean up names and then sort by name
    const keys = Object.keys(payload);
    keys.forEach( k => {
      if (k.slice(0, 3) === 'set') {
        payload[k.slice(3)] = payload[k];
        delete payload[k];
      }
      if (k.slice(0, 2) === '__') {
        delete payload[k];
      }
    })
    const orderedPayload = Object.entries(payload)
      .sort(([keyA, valueA], [keyB, valueB]) => keyA.localeCompare(keyB))
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
        }, {});
    const title = li.salesProductName + ' ==> ' + li.strategyName;
    const amId = li.adManagerLineitemId;
    const amStatus = li.adManagerStatus;
    const startDate = li.startDate.toString().slice(0, 10);
    const endDate = li.endDate.toString().slice(0, 10);
    let amState = (li.pushError) ? li.pushError : li.pushStatus;
    if (amState && amState.length > 100) {
      amState = amState.slice(0, 100);
    }
    let stateBadgeClass;
    if (amState === 'COMPLETED' || amState === 'UPDATED') {
      stateBadgeClass = classes.success;
    } else if (amState === 'NEEDS_UPDATE' || amState === 'NOT AD MANAGER') {
      stateBadgeClass = classes.warn;
    } else {
      stateBadgeClass = classes.error;
    }
    const amLink = GAM_LINE_PRIMARY.replace('{dfpLineItemId}', amId);
    const deliveryStatus = (li.deliveryIndicator !== null && li.deliveryIndicator !== -1) ? 'Started' : '';

    return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Settings className={classes.leftIcon} />
        <Typography variant="subtitle1">{title}
          <span>&nbsp; &nbsp;&nbsp; &nbsp;</span>
          {amId && placement.pushStatus !== 'NOT AD MANAGER' &&
          <Button
            target="_blank"
            href={amLink}
            className={classes.link}
            color="primary"
            size="small"
          >
            Ad Manager ID: {amId}
          </Button>}
          <span>&nbsp; &nbsp;&nbsp; &nbsp;</span>
          {amState && <Tooltip title={li.pushError}>
          <Chip label={amState} className={stateBadgeClass}/></Tooltip>}
          <span>&nbsp; &nbsp; &nbsp; &nbsp;</span>
          {amStatus && placement.pushStatus !== 'NOT AD MANAGER' &&
          <Chip label={amStatus} />
          }
          <span>&nbsp; &nbsp; &nbsp; &nbsp;{startDate} to {endDate} &nbsp;</span>
          {deliveryStatus && <Chip label={deliveryStatus} className={classes.warn} />}
          <Chip label={`Del Imp: ${li.amImpressions  || 0} Clk: ${li.amClicks || 0} CTR: ${li.amCTR || 0}`}/>
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        {(!isEmpty(orderedPayload) && placement.pushStatus !== 'NOT AD MANAGER') ? (
          <div className={classes.root}>
            {Object.keys(orderedPayload).map(k => (
              <Grid
                key={k}
                container
                spacing={4}
                direction="row"
                justifyContent="center"
                alignItems="stretch"
              >
                <Grid item xs={3}>
                  {this.renderLabel(k)}
                </Grid>
                <Grid item xs={9}>
                  {this.renderValue(k, orderedPayload)}
                </Grid>
              </Grid>
            )
          )}
            {!isEmpty(li.creativeTargetings) &&
              <div>
                <Grid
                  key={'creatives'}
                  container
                  spacing={4}
                  direction="row"
                  justifyContent="center"
                  alignItems="stretch"
                >
                  <Grid item xs={3}>
                    {this.renderLabel('Creative Level Targeting')}
                  </Grid>
                  <Grid item xs={9}>
                    <Card square key={'creativeTargetings'} className={classes.value}>
                      <Typography variant="subtitle1" className={classes.valueFont}>
                        {li.creativeTargetings}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              </div>}
          </div>
        ) : (
          <Typography variant="subtitle1" className={classes.top}>No Ad Manager Payload Available</Typography>
        )}

        </AccordionDetails>
      </Accordion>
    )
  }
}

AdManagerView.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(AdManagerView);
